import copy from "copy-to-clipboard";
import React, { useMemo, useState } from "react";

import { faClone } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCircleCheck, faVideo } from "@performyard/fontawesome/pro-solid-svg-icons";
import type { MeetingStatus } from "@performyard/models";
import { AvatarGroup, ButtonIcon, Chip, Icon, Text, TextDate, Tooltip } from "@performyard/py-components";

import ExpandableText from "../../../../components/ExpandableText";
import MeetingCalendarIcon from "./MeetingCalendarIcon";
import MeetingOptionsDropdown from "./MeetingOptionsDropdown";
import PostMeetingAnalysisButton from "./PostMeetingAnalysisButton";
import PostMeetingAnalysisPanel from "./PostMeetingAnalysisPanel";
import PreMeetingPrepButton from "./PreMeetingPrepButton";
import PreMeetingPrepPanel from "./PreMeetingPrepPanel";

import {
	getVideoConferencingProviderShortName,
	useVideoConferencingFeatureEnabled,
} from "../../../../api/hooks/defs/meetings-integrations";
import { usePostMeetingAnalysis, usePreMeetingPrep } from "../hooks";

import { toAvatarGroupEmployees } from "../../utils/participants";
import { formatMeetingTime, getParticipantName, isSameDay } from "../utils";

import { MEETING_STATUS_CONFIG, config } from "../config";
import { useMeetingContext } from "../context";

/** Copies the join link to the clipboard, briefly showing a green check on success (mirrors SCIM/API key). */
const CopyJoinLinkButton = ({ url }: { url: string }): React.ReactElement => {
	const [copied, setCopied] = useState(false);
	const [hovered, setHovered] = useState(false);

	const handleCopy = (): void => {
		copy(url);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	// `copied` OR-keeps the tooltip open through the click so the success label shows.
	return (
		<Tooltip
			content={copied ? "Link Copied" : "Copy Meeting Link"}
			placement="top"
			open={hovered || copied}
			onOpenChange={setHovered}
			asChild
		>
			<ButtonIcon
				variant="standard"
				color={copied ? "green" : "neutral"}
				shade={5}
				size={16}
				icon={copied ? faCircleCheck : faClone}
				onClick={handleCopy}
				aria-label="Copy meeting link"
			/>
		</Tooltip>
	);
};

/**
 * Header section displaying meeting details: name, participants, status, and options.
 */
const MeetingDetailsHeader = (): React.ReactElement => {
	const { meeting, userId, setEditingMeeting, isAdminView, workspace, recurrenceLabel, participantNames } =
		useMeetingContext();
	const preMeetingPrep = usePreMeetingPrep();
	const postMeetingAnalysis = usePostMeetingAnalysis();

	const hasDescription = Boolean(meeting.description?.trim());
	const videoConferencingFeatureEnabled = useVideoConferencingFeatureEnabled();
	const joinUrl = videoConferencingFeatureEnabled ? meeting.videoConference?.joinUrl : undefined;
	const providerDisplayName = getVideoConferencingProviderShortName(meeting.videoConference?.provider);
	const isTeamMeeting = meeting.participants.length > 2;

	const participantEmployees = useMemo(
		() =>
			toAvatarGroupEmployees(meeting.participants, {
				currentUserId: userId,
				resolveName: (id) => getParticipantName(workspace, participantNames, id),
			}),
		[meeting.participants, userId, workspace, participantNames]
	);

	const getStatusChip = (status: MeetingStatus): React.ReactElement => {
		const { label, color } = MEETING_STATUS_CONFIG[status];
		return <Chip label={label} color={color} size={24} />;
	};

	const renderMeetingDateTime = (): React.ReactElement => {
		const startDateTime = new Date(meeting.startDateTime);
		const endDateTime = new Date(meeting.endDateTime);

		if (!isSameDay(startDateTime, endDateTime)) {
			return (
				<div className="flex items-center gap-1">
					<TextDate date={startDateTime} dateSize="medium" hasTime />
					<Text size="paragraph-3" weight="regular">
						–
					</Text>
					<TextDate date={endDateTime} dateSize="medium" hasTime />
				</div>
			);
		}

		return (
			<div className="flex items-center gap-2">
				<TextDate date={startDateTime} dateSize="medium" hasTime={false} />
				<Text size="paragraph-3" weight="regular">
					{formatMeetingTime(startDateTime)} – {formatMeetingTime(endDateTime)}
				</Text>
			</div>
		);
	};

	const metaDivider = (
		<Text size="paragraph-3" weight="regular" color="neutral-5">
			|
		</Text>
	);

	const showPreMeetingPrep = preMeetingPrep.isVisible && !isTeamMeeting;
	const showPostMeetingAnalysis = postMeetingAnalysis.isVisible && !isTeamMeeting;

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-1">
				<div className="flex items-center gap-2">
					<Text size="heading-5" weight="semi-bold" dangerouslySetClassName="py-line-clamp-1">
						{meeting.name}
					</Text>
					{meeting.status !== "active" && (
						<div className="flex-shrink-0">{getStatusChip(meeting.status)}</div>
					)}
					{!isAdminView && (
						<div className="ml-auto flex items-center gap-2">
							{showPreMeetingPrep && <PreMeetingPrepButton onClick={preMeetingPrep.handleOpen} />}
							{showPostMeetingAnalysis && (
								<PostMeetingAnalysisButton onClick={postMeetingAnalysis.handleOpen} />
							)}
							<MeetingOptionsDropdown meeting={meeting} setEditingMeeting={setEditingMeeting} />
						</div>
					)}
				</div>
				<div className="flex items-center gap-1">
					<div className="flex items-center gap-1">
						<MeetingCalendarIcon meeting={meeting} />
						{renderMeetingDateTime()}
						{recurrenceLabel && (
							<Text size="paragraph-3" weight="regular">
								· {recurrenceLabel}
							</Text>
						)}
					</div>
					{metaDivider}
					<AvatarGroup
						employees={participantEmployees}
						hasLinks={true}
						maxLength={participantEmployees.length <= 5 ? 5 : 4}
						size={config.layout.dropdownAvatarSize}
						employeeNameTooltip={true}
						modalTitle="Meeting Participants"
					/>
					{joinUrl && (
						<>
							{metaDivider}
							<a
								href={joinUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-1 whitespace-nowrap text-primary-5 no-underline hover:underline"
							>
								<Icon size={16} icon={faVideo} color="primary-5" />
								<Text size="paragraph-3" weight="semi-bold" color="primary-5">
									{providerDisplayName ? `Join ${providerDisplayName} Meeting` : "Join Meeting"}
								</Text>
							</a>
							<CopyJoinLinkButton url={joinUrl} />
						</>
					)}
				</div>
			</div>

			{hasDescription && (
				<ExpandableText content={meeting.description} collapseHeight={config.descriptionCollapseHeight} />
			)}

			<PreMeetingPrepPanel {...preMeetingPrep} />
			<PostMeetingAnalysisPanel {...postMeetingAnalysis} />
		</div>
	);
};

export default MeetingDetailsHeader;
